import React from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import HowItWorks from "../components/HowItWorks";
import Brands from "../components/Brands";
import { useQuote } from "../context/QuoteContext";
import { Check, ArrowRight, MapPin, Phone, Star } from "lucide-react";
import { SITE_URL } from "../lib/site";
import { company, testimonials } from "../data/mock";
import { serviceAreas, AREA_SERVICES, TV_AREA_SLUGS } from "../data/serviceAreas";
import ReviewStars from "../components/ReviewStars";
import { useContent, fill } from "../lib/content";
import { LocationQuoteCTA } from "../components/LocationQuoteCTA";
import { TvMountingLocal, tvFaqs, tvTitle } from "../components/TvMountingLocal";
import { TownInstalls } from "../components/InstallGallery";

export { TV_AREA_SLUGS };

const WHY = [
  "Local, same-week scheduling across Eastern NC",
  "Fully licensed & insured technicians",
  "Clean cable concealment & tidy finish",
  "5.0-star rated",
];

const ServiceAreaPage = ({ slug }) => {
  const { openQuote } = useQuote();
  const content = useContent();
  const area = serviceAreas.find((a) => a.slug === slug);
  if (!area) return <main className="max-w-2xl mx-auto px-5 py-24 text-center text-slate-500">Area not found. <Link to="/service-areas" className="text-[#3b9dff] font-semibold">See all areas →</Link></main>;

  const areaIdx = serviceAreas.findIndex((a) => a.slug === slug);
  const picks = [testimonials[(areaIdx * 2) % testimonials.length], testimonials[(areaIdx * 2 + 1) % testimonials.length]];

  const path = `/${area.slug}-nc`;
  const pageUrl = `${SITE_URL}${path}`;
  const title = area.title || `AV & Home Theater Installation in ${area.city}, NC`;
  const metaDescription = `Professional TV mounting, home theater, whole-home audio and smart home installation in ${area.city}, ${area.county}, NC. Local, licensed & insured. 5.0-star rated. Get a free quote from Set Up Home Theater.`;

  const faqs = content.areaFaq.map((f) => {
    const v = { city: area.city, county: area.county, region: area.region, nearby: area.nearby.join(", ") };
    return { q: fill(f.q, content, v), a: fill(f.a, content, v) };
  });

  const serviceSchema = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: "Audio-Visual & Home Theater Installation",
    name: title, description: metaDescription, url: pageUrl, image: area.image,
    areaServed: { "@type": "City", name: `${area.city}, North Carolina` },
    provider: {
      "@type": "LocalBusiness", "@id": `${SITE_URL}/#business`, name: "Set Up Home Theater",
      telephone: "+1-252-545-0011", email: "chris@setuphometheater.com", url: SITE_URL, priceRange: "$$",
      address: { "@type": "PostalAddress", addressLocality: "Rocky Mount", addressRegion: "NC", addressCountry: "US" },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "21", bestRating: "5" },
      review: picks.map((t) => ({ "@type": "Review", author: { "@type": "Person", name: t.name }, reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" }, reviewBody: t.text })),
    },
    offers: AREA_SERVICES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.name, url: `${SITE_URL}${s.to}` } })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/service-areas` },
      { "@type": "ListItem", position: 3, name: `${area.city}, NC`, item: pageUrl },
    ],
  };
  const isTv = TV_AREA_SLUGS.includes(area.slug);
  const tvQa = isTv ? tvFaqs(content, area) : [];
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: [...tvQa, ...faqs].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <main data-testid={`area-page-${area.slug}`}>
      <SEO title={title} fullTitle={isTv ? tvTitle(area.city, content.prices.sizes[0]) : undefined} description={metaDescription} path={path} image={area.image} jsonLd={[serviceSchema, breadcrumbSchema, faqSchema]} />
      <PageHeader
        breadcrumb="Home / Service Areas"
        title={title}
        subtitle={`Local, licensed and insured audio-visual installation serving ${area.city} and ${area.county}, North Carolina.`}
      >
        <LocationQuoteCTA />
      </PageHeader>

      {/* Answer-first intro (GEO/SXO) */}
      <section className="section-pad">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div className="rounded-2xl overflow-hidden shadow-2xl lg:sticky lg:top-28">
            <img src={area.image} alt={`Home theater and TV installation in ${area.city}, NC`} className="w-full h-[380px] object-cover" loading="lazy" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 eyebrow"><MapPin className="w-4 h-4 text-[#3b9dff]" /> Serving {area.city}, NC</span>
            <ReviewStars className="mt-3" />
            <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-4">Your local AV installers in {area.city}</h2>
            {area.body ? <p data-testid="area-body" className="text-slate-600 leading-relaxed mb-5">{area.body}</p> : <p className="text-slate-600 leading-relaxed mb-5">
              <strong>Set Up Home Theater</strong> designs and installs TVs, home theaters, whole-home audio, commercial AV
              and smart home systems for homeowners and businesses in {area.city}, {area.county} and nearby {area.nearby.join(", ")}.
              We handle everything (mounting, wiring, cable concealment and calibration) for a clean, professional finish.
            </p>}
            <div className="grid sm:grid-cols-2 gap-3 mb-7">
              {WHY.map((f) => (
                <div key={f} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#eef4ff] flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-[#3b9dff]" /></span>
                  <span className="text-[#0a1f44] font-medium text-sm">{f}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => openQuote("", "", `${area.city}, NC`)} data-testid="area-quote-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors">Get a Free Quote <ArrowRight className="w-4 h-4" /></button>
              <a href={company.phoneLink} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#3b9dff]/40 text-[#3b9dff] font-semibold hover:bg-[#eef4ff] transition-colors"><Phone className="w-4 h-4" /> {company.phone}</a>
            </div>
          </div>
        </div>
      </section>

      {isTv && <TvMountingLocal city={area.city} county={area.county} nearby={area.nearby} />}
      <TownInstalls town={area.slug} city={area.city} />

      {/* Services offered — internal links to conversion pages */}
      <section className="section-pad bg-[#f5f8ff]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          <span className="eyebrow">What we install</span>
          <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-8">AV services available in {area.city}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AREA_SERVICES.map((s) => (
              <Link key={s.to} to={s.to} data-testid={`area-service-${s.to}`} className="group bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center justify-between gap-3">
                <span className="font-semibold text-[#0a1f44]">{s.name}</span>
                <ArrowRight className="w-4 h-4 text-[#3b9dff] group-hover:translate-x-1 transition-transform shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ (GEO — FAQPage schema above) */}
      <section className="section-pad" data-testid="area-faq">
        <div className="max-w-[860px] mx-auto px-5 lg:px-8">
          <span className="eyebrow">FAQs</span>
          <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-8">AV installation in {area.city}, quick answers</h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group bg-white rounded-xl border border-slate-200 p-5 open:shadow-md transition-shadow" data-testid={`area-faq-${i}`}>
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-bold text-[#0a1f44]">
                  <span>{f.q}</span>
                  <span className="text-[#3b9dff] text-xl leading-none shrink-0 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-slate-500 leading-relaxed text-sm mt-3">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 5.0-star rated across Eastern North Carolina.</div>
        </div>
      </section>

      {/* Local testimonials (real 5-star reviews) */}
      <section className="section-pad bg-[#f7faff]" data-testid="area-testimonials">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8">
          <div className="text-center">
            <span className="eyebrow">5.0-star rated</span>
            <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4">Why Eastern NC homeowners trust us</h2>
            <ReviewStars className="mx-auto mt-4" starClass="w-5 h-5" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-9">
            {picks.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col">
                <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#fbbf24] text-[#fbbf24]" />)}</div>
                <p className="text-slate-600 text-sm leading-relaxed line-clamp-5">“{t.text}”</p>
                <p className="mt-4 font-semibold text-[#0a1f44] text-sm">{t.name} <span className="text-slate-400 font-normal">· Verified Google review</span></p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href={company.googleReview} target="_blank" rel="noopener noreferrer" data-testid="area-leave-review-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0a1f44] text-white text-sm font-semibold hover:bg-[#12305f] transition-colors"><Star className="w-4 h-4 fill-white text-white" /> Leave us a Review on Google</a>
          </div>
        </div>
      </section>

      <HowItWorks />
      <Brands />
    </main>
  );
};

export default ServiceAreaPage;
